import "./styles/main.css";
import { HomeScreen } from "./ui/HomeScreen";
import { PlayerScreen } from "./ui/PlayerScreen";
import {
  answerWorkerRequests,
  openPickedFile,
  openStoredWalk,
  startWalk,
  stopWalk,
  UnreadableWalkError,
  type OpenedWalk,
} from "./walk/openWalk";
import {
  clearAllWalks,
  deleteWalk,
  getStorageUsage,
  listWalks,
  renameWalk,
  requestPersistentStorage,
} from "./walk/storage";
import { clearLoading, deviceGpuLimitMb, interruptedLoadText, markLoading, memoryWarningText, needsMemoryWarning, takeInterruptedLoad } from "./walk/memory";
import { VIEWER_FAILED_MESSAGE } from "./walk/loadWatch";
import { fetchWalk, walkUrlFromSearch, withoutWalkParam } from "./walk/remoteWalk";
import { consumePendingSharedFile } from "./pwa/shareTarget";
import { AppUpdater } from "./pwa/updates";
import { VersionFooter } from "./ui/VersionFooter";
import { createWalkPlayerApi } from "./walk/playerApi";
import { diagMark, installCapture } from "./diag/capture";
import { diagViewerSettled, diagWalkClosed, diagWalkFailed, diagWalkOpening, diagWalkShown } from "./diag/state";
import { DiagnosticsPanel } from "./ui/DiagnosticsPanel";
import { AdvancedSheet } from "./ui/AdvancedSheet";
import { buttonView, INITIAL_STATUS, type UpdateStatus } from "./pwa/updateFlow";

installCapture(); // before anything else runs: the event log should see all of it
const diagnostics = new DiagnosticsPanel();

const root = document.getElementById("app");
if (!root) throw new Error("Missing #app root element");

const appTitle = document.title;
let opening = false;
/** The walk now open (or opening), so a viewer that fails to start can be retried. */
let current: { id: string; name: string; stored: boolean } | null = null;

const home = new HomeScreen({
  onFile: (file) => void openFile(file),
  onOpenStored: (id) => void openFile(id, true),
  onRenameStored: (id, name) => void renameWalk(id, name).then(refreshSavedWalks),
  onDeleteStored: (id) => void deleteWalk(id).then(refreshSavedWalks),
  onClearAll: () => void clearAllWalks().then(refreshSavedWalks),
});
const player = new PlayerScreen({
  onExit: () => closeWalk(),
  onViewerSettled: () => {
    clearLoading(localStorage);
    diagViewerSettled();
  },
  onViewerFailed: () => viewerFailed(),
  onDiagnostics: () => diagnostics.open(),
  onAdvanced: () => advanced.open(),
});
let updater: AppUpdater | null = null;
let updateStatus: UpdateStatus = INITIAL_STATUS;
// The ⚙ panel's and the footer's Advanced / Debug sheet (src/ui/AdvancedSheet.ts).
const advanced = new AdvancedSheet({
  openDiagnostics: () => diagnostics.open(),
  walkOpen: () => !player.element.hidden,
  reloadViewer: () => reloadViewer(),
  viewerWindow: () => player.viewerWindow(),
  tablet: () => player.tabletSettings(),
  setTablet: (key, on) => player.setTabletOption(key, on),
  updateText: () => {
    const view = buttonView(updateStatus, __APP_VERSION__);
    return view.note ? `${view.label} — ${view.note}` : view.label;
  },
  checkForUpdates: async () => void (await updater?.check()),
  updateNow: async () => void (await (updateStatus.phase === "ready" ? updater?.apply() : updater?.check())),
  savedWalksChanged: () => void refreshSavedWalks(),
});
const footer = new VersionFooter((phase) => {
  if (!updater) return;
  if (phase === "ready") void updater.apply();
  else if (phase === "failed") void updater.retry();
  else void updater.check();
}, () => diagnostics.open(), () => advanced.open());
home.element.querySelector(".home-card")?.append(footer.element);
root.append(home.element, player.element);
// For headless checks: window.walkPlayer.textureFormat() / .fps(), null while no walk is open.
(window as unknown as { walkPlayer: unknown }).walkPlayer = createWalkPlayerApi(() => player.viewerWindow());

if (!("serviceWorker" in navigator)) {
  footer.hideUpdateButton();
  home.showError("This browser cannot play walks offline. Use Safari, Chrome or Edge.");
} else {
  updater = new AppUpdater({
    onStatus: (status) => {
      footer.setStatus(status);
      updateStatus = status;
      diagMark("update", status.phase + (status.latest ? ` latest=${status.latest}` : ""));
    },
    onOfflineState: (state) => footer.setOfflineState(state),
    // Never reload under an open walk; closeWalk() retries.
    isIdle: () => !opening && player.element.hidden,
  });
  answerWorkerRequests();
  void requestPersistentStorage();
  void refreshSavedWalks();
  // The last walk never finished loading: iOS closes a page that uses too much memory, silently.
  const interrupted = takeInterruptedLoad(localStorage);
  if (interrupted) void home.notify(interruptedLoadText(interrupted, deviceGpuLimitMb(navigator.userAgent, navigator.maxTouchPoints)));
}

async function refreshSavedWalks(): Promise<void> {
  const [walks, usage] = await Promise.all([listWalks(), getStorageUsage()]);
  home.renderSavedWalks(walks, usage);
}

/** source is either a freshly picked File, or a stored walk's id when fromStorage is true. */
async function openFile(source: File | string, fromStorage = false, save = true): Promise<void> {
  if (opening) return;
  opening = true;
  home.setBusy(true, typeof source === "string" ? "Opening…" : `Opening ${source.name}…`);
  try {
    const walk: OpenedWalk = fromStorage
      ? await openStoredWalk(source as string)
      : await openPickedFile(source as File, { save });
    if (needsMemoryWarning(walk.gpuMb, deviceGpuLimitMb(navigator.userAgent, navigator.maxTouchPoints))) {
      home.setBusy(false);
      if (!(await home.confirmOpen(memoryWarningText(walk.gpuMb as number)))) return;
      home.setBusy(true, `Opening ${walk.name}…`);
    }
    // Cleared when the viewer reports ready (or errors), or the walk is closed.
    markLoading(localStorage, { id: walk.id, name: walk.name, gpuMb: walk.gpuMb, at: Date.now() });
    diagWalkOpening(walk);
    current = { id: walk.id, name: walk.name, stored: walk.stored };
    await startWalk(walk);
    home.setBusy(false);
    home.element.hidden = true;
    player.show(walk.url, walk.name, walk.gpuMb, walk.preset);
    diagWalkShown();
    if (!walk.stored && !walk.temporary) {
      // home is already hidden, so an inline status message would go
      // unseen - this needs to interrupt, since it means the walk won't
      // be reopenable offline without picking the file again.
      alert("Opened, but could not save this walk on the device (storage full?). It will not be available offline next time - pick the file again if needed.");
    }
  } catch (err) {
    clearLoading(localStorage); // opening failed here, not a page killed while loading
    diagWalkFailed((err as Error).message);
    stopWalk();
    if (err instanceof UnreadableWalkError) {
      const { walkId } = err;
      home.showError(err.message, {
        label: "Remove",
        run: () => void deleteWalk(walkId).then(() => { home.setBusy(false); return refreshSavedWalks(); }),
      });
    } else {
      home.showError((err as Error).message);
    }
  } finally {
    opening = false;
    void refreshSavedWalks();
    updater?.applyIfIdle(); // opening failed: back on home, nothing to interrupt
  }
}

/** The viewer iframe never started (error page, no viewer script): home again, with the error and Try again. */
function viewerFailed(): void {
  const failed = current;
  diagMark("viewer-failed", "the viewer iframe never started");
  closeWalk();
  const name = failed?.name ?? "this walk";
  const id = failed?.id;
  home.showError(
    VIEWER_FAILED_MESSAGE(name),
    failed?.stored && id ? { label: "Try again", run: () => void openFile(id, true) } : undefined,
  );
}

/** Advanced menu: load the open walk's viewer again (switches changed). The loading flag is set again, like a fresh open. */
function reloadViewer(): void {
  if (current) markLoading(localStorage, { id: current.id, name: current.name, at: Date.now() });
  diagMark("viewer-reload", current?.name ?? "");
  player.reloadViewer();
}

function closeWalk(): void {
  diagWalkClosed();
  current = null;
  clearLoading(localStorage);
  player.hide();
  stopWalk();
  home.element.hidden = false;
  document.title = appTitle;
  void refreshSavedWalks();
  updater?.applyIfIdle();
}

addEventListener("keydown", (e) => {
  if (e.key === "Escape" && !player.element.hidden && !player.isKiosk) closeWalk();
});

if (new URLSearchParams(location.search).get("diagnostics") === "1") diagnostics.open();

/** ?walk=<url>: Walk Bake's Preview Walk. Fetched and played for this visit only; not added to the saved walks. */
async function openWalkFromUrl(url: URL): Promise<void> {
  history.replaceState(null, "", location.pathname + withoutWalkParam(location.search) + location.hash);
  home.setBusy(true, `Downloading ${url.host}…`);
  try {
    await openFile(await fetchWalk(url), false, false);
  } catch (err) {
    home.showError((err as Error).message);
  }
}

const walkUrl = walkUrlFromSearch(location.search, location.href);
if (walkUrl) void openWalkFromUrl(walkUrl);
else void consumePendingSharedFile().then((file) => file && openFile(file));
